// src/lib/sample-data-utils.ts
import Papa from "papaparse";
import { db } from "@/services/db";
import {
  StorageData,
  DailyData,
  HourlyData,
  CampaignSettings,
  ProductData,
  SearchTermData,
  PMaxData,
  AccountInfo,
  QualityData,
  PlacementData,
  KeywordData,
  CampConvData,
  Campaign,
  CampaignIdsWithSpendData,
  ChangesData,
  AcctOverviewData,
  ConvTrendsData,
  SearchTermInsightRow,
  KwCpcData,
  AssetsData,
  PMaxAssetsData,
  AdAssetsData,
  CampAssetsData,
  AgAssetsData,
  GeoPerfData,
  LPagePerfData,
  CampaignBudgetData,
  WeeklyPerformanceData,
  SmartBiddingChangesData,
  ConvActionData,
  DgNetworkPerfData,
  DgAdsData,
  DgAssetsData,
} from "@/types/metrics";
import {
  OnPerformanceData,
  OnSettingsData,
  OnDisapprovedData,
  OnEmptyAdGroupsData,
} from "@/types/onboarding";
import {
  SHEET_TABS,
  SAMPLE_ACCOUNT_ID,
  SAMPLE_ACCOUNT_SCRIPT_VERSION,
} from "./constants";

interface LocalBaseMetrics {
  impr: number;
  clicks: number;
  cost: number;
  conv: number;
  value: number;
  imprShare?: number;
  lostBudget?: number;
  lostRank?: number;
}

// Helper function to sanitize strings in an object
const sanitizeStrings = (obj: Record<string, any>): Record<string, any> => {
  const result: Record<string, any> = {};
  for (const [key, value] of Object.entries(obj)) {
    result[key] = typeof value === "string" ? value.trim() : value;
  }
  return result;
};

// Helper to handle both cases for metric names
export const getMetricValue = (
  row: Record<string, any>,
  camelCase: string,
  pascalCase: string,
): number => {
  return Number(row[camelCase] || row[pascalCase] || 0);
};

export const transformBaseMetrics = (
  row: Record<string, any>,
): LocalBaseMetrics & {
  CTR: number;
  CvR: number;
  ROAS: number;
  CPA: number;
  AOV: number;
  profit: number;
} => {
  const impr = Number(row.Impressions || row.impr || 0);
  const clicks = Number(row.Clicks || row.clicks || 0);
  const cost = Number(row.Cost || row.cost || 0);
  const conv = Number(row.Conversions || row.conv || 0);
  const value = Number(
    row.ConvValue || row.value || row["Conversion Value"] || 0,
  );
  const imprShare = Number(row.ImprShare || row.imprShare || 0);
  const lostBudget = Number(row.LostToBudget || row.lostBudget || 0);
  const lostRank = Number(row.LostToRank || row.lostRank || 0);

  return {
    impr,
    clicks,
    cost,
    conv,
    value,
    imprShare,
    lostBudget,
    lostRank,
    CTR: impr ? (clicks / impr) * 100 : 0,
    CvR: clicks ? (conv / clicks) * 100 : 0,
    ROAS: cost ? value / cost : 0,
    CPA: conv ? cost / conv : 0,
    AOV: conv ? value / conv : 0,
    profit: value - cost,
  };
};

type DataTransformer<T> = (data: Record<string, any>[]) => T[];

export const transformers: {
  [key in keyof typeof SHEET_TABS]: DataTransformer<
    key extends "ACCOUNT"
      ? AccountInfo
      : key extends "DAILY"
        ? DailyData
        : key extends "THIRTY_DAYS"
          ? DailyData
          : key extends "P_THIRTY_DAYS"
            ? DailyData
            : key extends "SEVEN_DAYS"
              ? DailyData
              : key extends "P_SEVEN_DAYS"
                ? DailyData
                : key extends "ACCT_OVERVIEW"
                  ? AcctOverviewData
                  : key extends "HOURLY"
                    ? HourlyData
                    : key extends "HOURLY_YEST"
                      ? HourlyData
                      : key extends "H_CAMP_30D"
                        ? HourlyData
                        : key extends "H_CAMP_7D"
                          ? HourlyData
                          : key extends "H_DOW_30D"
                            ? HourlyData
                            : key extends "H_DOW_7D"
                              ? HourlyData
                              : key extends "SETTINGS"
                                ? CampaignSettings
                                : key extends "BIDDING"
                                  ? CampaignSettings
                                  : key extends "PMAX_PERF"
                                    ? PMaxData
                                    : key extends "PRODUCTS"
                                      ? ProductData
                                      : key extends "SEARCH80"
                                        ? SearchTermData
                                        : key extends "PLACEMENTS"
                                          ? PlacementData
                                          : key extends "QUALITY"
                                            ? QualityData
                                            : key extends "KEYWORD80"
                                              ? KeywordData
                                              : key extends "KEYWORD80_PREV"
                                                ? KeywordData
                                                : key extends "CAMP_CONV"
                                                  ? CampConvData
                                                  : key extends "CONV_ACTION"
                                                    ? ConvActionData
                                                    : key extends "CAMPAIGN_IDS_SPEND"
                                                        ? CampaignIdsWithSpendData
                                                        : key extends "CHANGES"
                                                          ? ChangesData
                                                          : key extends "CONV_TRENDS"
                                                            ? ConvTrendsData
                                                            : key extends "ST_INSIGHTS"
                                                              ? SearchTermInsightRow
                                                              : key extends "KW_CPC"
                                                                ? KwCpcData
                                                                : key extends "ASSETS"
                                                                  ? AssetsData
                                                                  : key extends "PMAX_ASSETS"
                                                                    ? PMaxAssetsData
                                                                    : key extends "AD_ASSETS"
                                                                      ? AdAssetsData
                                                                      : key extends "CAMP_ASSETS"
                                                                        ? CampAssetsData
                                                                        : key extends "AG_ASSETS"
                                                                          ? AgAssetsData
                                                                          : key extends "GEO_PERF"
                                                                            ? GeoPerfData
                                                                            : key extends "LPAGE_PERF"
                                                                              ? LPagePerfData
                                                                              : key extends "CAMPAIGN_BUDGETS"
                                                                                ? CampaignBudgetData
                                                                                : key extends "WEEKLY_PERFORMANCE"
                                                                                  ? WeeklyPerformanceData
                                                                                  : key extends "SMART_BIDDING_CHANGES"
                                                                                    ? SmartBiddingChangesData
                                                                                    : key extends "DG_NETWORK_PERF"
                                                                                      ? DgNetworkPerfData
                                                                                      : key extends "DG_ADS"
                                                                                        ? DgAdsData
                                                                                        : key extends "DG_ASSETS"
                                                                                          ? DgAssetsData
                                                                                          : key extends "ON_PERFORMANCE"
                                                                                            ? OnPerformanceData
                                                                                            : key extends "ON_SETTINGS"
                                                                                              ? OnSettingsData
                                                                                              : key extends "ON_DISAPPROVED"
                                                                                                ? OnDisapprovedData
                                                                                                : key extends "ON_EMPTY_AD_GROUPS"
                                                                                                  ? OnEmptyAdGroupsData
                                                                                                  : never
  >;
} = {
  ACCOUNT: (data): AccountInfo[] => {
    const accountInfo = [
      {
        accountName: "Sample Account",
        scriptVersion: SAMPLE_ACCOUNT_SCRIPT_VERSION,
        ocid: "12345678",
      },
    ];
    return accountInfo;
  },

  DAILY: (data) => {
    const result = data.map((row) => {
      const sanitized = sanitizeStrings(row);
      const metrics = transformBaseMetrics(sanitized);
      const date = String(
        sanitized.date || sanitized.Date || new Date().toISOString(),
      );
      const campaign = String(sanitized.campaign || sanitized.Campaign || "");
      const campaignId = String(
        sanitized.campaignId || sanitized.CampaignId || "",
      );

      return {
        ...metrics,
        imprShare: metrics.imprShare || 0,
        lostBudget: metrics.lostBudget || 0,
        lostRank: metrics.lostRank || 0,
        campaign,
        campaignId,
        date,
        Date: date,
        Campaign: campaign,
        CampaignId: campaignId,
      };
    });

    return result;
  },

  THIRTY_DAYS: (data): DailyData[] => transformers.DAILY(data),
  P_THIRTY_DAYS: (data): DailyData[] => transformers.DAILY(data),
  SEVEN_DAYS: (data): DailyData[] => transformers.DAILY(data),
  P_SEVEN_DAYS: (data): DailyData[] => transformers.DAILY(data),

  HOURLY: (data): HourlyData[] => {
    return data.map((row) => ({
      campaign: String(row.campaign || ""),
      Campaign: String(row.campaign || ""),
      campaignId: String(row.campaignId || ""),
      hour: Number(row.hour || 0),
      Hour: Number(row.hour || 0),
      days: Number(row.days || 0),
      impr: Number(row.impr || 0),
      clicks: Number(row.clicks || 0),
      cost: Number(row.cost || 0),
      conv: Number(row.conv || 0),
      value: Number(row.value || 0),
      imprShare: 0,
      lostBudget: 0,
      lostRank: 0,
      CTR: 0,
      CvR: 0,
      ROAS: 0,
      CPA: 0,
      AOV: 0,
      profit: 0,
    }));
  },

  HOURLY_YEST: (data): HourlyData[] => transformers.HOURLY(data),

  SETTINGS: (data): CampaignSettings[] =>
    data.map((row) => ({
      campaign: String(row.Campaign || row.campaign || ""),
      campaignId: String(row.CampaignId || row.campaignId || ""),
      status: String(row.Status || row.status || ""),
      budget: String(row.Budget || row.budget || ""),
      bidStrategy: String(row.BidStrategy || row.bidStrategy || ""),
      bidStatus: String(row.BidStatus || row.bidStatus || ""),
      bidType: String(row.BidType || row.bidType || ""),
      targetCpa: Number(row.TargetCPA || row.targetCpa || 0),
      targetRoas: Number(row.TargetROAS || row.targetRoas || 0),
      maxCpc: Number(row.MaxCPC || row.maxCpc || 0),
      maxCpv: Number(row.MaxCPV || row.maxCpv || 0),
      labels: String(row.Labels || row.labels || ""),
      notes: String(row.Notes || row.notes || ""),
      channel: String(row.Channel || row.channel || ""),
      subChannel: String(row.SubChannel || row.subChannel || ""),
      optStatus: String(row.OptStatus || row.optStatus || ""),
      rtbOptIn: Boolean(
        row.RTBOptIn || row.rtbOptIn || row.RtbOptIn || row.rtb_opt_in,
      ),
      statusReasons: String(row.StatusReasons || row.statusReasons || ""),
      primaryStatus: String(row.PrimaryStatus || row.primaryStatus || ""),
      servingStatus: String(row.ServingStatus || row.servingStatus || ""),
      urlOptOut: Boolean(row.UrlOptOut || row.urlOptOut),
      group: String(row.Group || row.group || ""),
      excludedTypes: String(row.ExcludedTypes || row.excludedTypes || ""),
      includedTypes: String(row.IncludedTypes || row.includedTypes || ""),
      targetImprShare: Number(row.TargetImprShare || row.targetImprShare || 0),
      excludedPlacements: String(
        row.ExcludedPlacements || row.excludedPlacements || "",
      ),
      campaignType: String(row.CampaignType || row.campaignType || ""),
    })),

  PRODUCTS: (data): ProductData[] =>
    data.map((row) => {
      const metrics = transformBaseMetrics(row);
      return {
        campaign: String(row.campaign || row.Campaign || ""),
        campaignId: String(row.campaignId || row.CampaignId || ""),
        impressions: metrics.impr,
        clicks: metrics.clicks,
        cost: metrics.cost,
        conversions: metrics.conv,
        convValue: metrics.value,
        imprShare: metrics.imprShare || 0,
        lostToBudget: metrics.lostBudget || 0,
        lostRank: metrics.lostRank || 0,
        ProductId: String(row.ProductId || ""),
        ProductTitle: String(row.ProductTitle || ""),
      };
    }),

  PMAX_PERF: (data): PMaxData[] =>
    data.map((row) => ({
      Date: String(row.Date || row.date || ""),
      Campaign: String(row.Campaign || row.campaign || ""),
      "Search Cost": Number(row["Search Cost"] || row.SearchCost || 0),
      "Search Conv": Number(row["Search Conv"] || row.SearchConv || 0),
      "Search Value": Number(row["Search Value"] || row.SearchValue || 0),
      "Display Cost": Number(row["Display Cost"] || row.DisplayCost || 0),
      "Display Conv": Number(row["Display Conv"] || row.DisplayConv || 0),
      "Display Value": Number(row["Display Value"] || row.DisplayValue || 0),
      "Video Cost": Number(row["Video Cost"] || row.VideoCost || 0),
      "Video Conv": Number(row["Video Conv"] || row.VideoConv || 0),
      "Video Value": Number(row["Video Value"] || row.VideoValue || 0),
      "Shopping Cost": Number(row["Shopping Cost"] || row.ShoppingCost || 0),
      "Shopping Conv": Number(row["Shopping Conv"] || row.ShoppingConv || 0),
      "Shopping Value": Number(row["Shopping Value"] || row.ShoppingValue || 0),
      "Total Cost": Number(row["Total Cost"] || row.TotalCost || 0),
      "Total Conv": Number(row["Total Conv"] || row.TotalConv || 0),
      "Total Value": Number(row["Total Value"] || row.TotalValue || 0),
    })),

  PLACEMENTS: (data): PlacementData[] =>
    data.map((row) => ({
      displayName: String(row["Display Name"] || row.displayName || ""),
      placement: String(row.Placement || row.placement || ""),
      type: String(row.Type || row.type || ""),
      targetUrl: String(row["Target URL"] || row.targetUrl || ""),
      campaign: String(row.Campaign || row.campaign || ""),
      impressions: Number(
        String(row.Impressions || row.impressions || "0").replace(/,/g, ""),
      ),
    })),

  H_CAMP_30D: (data): HourlyData[] => {
    return data.map((row) => ({
      campaign: row.campaign,
      Campaign: row.campaign,
      campaignId: String(row.campaignId || ""),
      hour: Number(row.hour || 0),
      Hour: Number(row.hour || 0),
      days: Number(row.days || 0),
      impr: Number(row.impr || 0),
      clicks: Number(row.clicks || 0),
      cost: Number(row.cost || 0),
      conv: Number(row.conv || 0),
      value: Number(row.value || 0),
      imprShare: 0,
      lostBudget: 0,
      lostRank: 0,
      CTR: 0,
      CvR: 0,
      ROAS: 0,
      CPA: 0,
      AOV: 0,
      profit: 0,
    }));
  },

  H_CAMP_7D: (data): HourlyData[] => {
    return data.map((row) => ({
      campaign: String(row.campaign || ""),
      campaignId: String(row.campaignId || ""),
      hour: Number(row.hour || 0),
      Hour: Number(row.hour || 0),
      days: Number(row.days || 0),
      impr: Number(row.impr || 0),
      clicks: Number(row.clicks || 0),
      cost: Number(row.cost || 0),
      conv: Number(row.conv || 0),
      value: Number(row.value || 0),
      imprShare: 0,
      lostBudget: 0,
      lostRank: 0,
      CTR: 0,
      CvR: 0,
      ROAS: 0,
      CPA: 0,
      AOV: 0,
      profit: 0,
    }));
  },

  H_DOW_30D: (data): HourlyData[] => {
    return data.map((row) => {
      const dowNum =
        typeof row.dow === "string"
          ? getDayNumber(row.dow)
          : Number(row.dow || 0);

      return {
        campaign: String(row.campaign || "(All Campaigns)"),
        campaignId: String(row.campaignId || "ALL"),
        hour: Number(row.hour || 0),
        Hour: Number(row.hour || 0),
        dow: dowNum,
        DayOfWeek: dowNum,
        days: Number(row.days || 0),
        impr: Number(row.impr || 0),
        clicks: Number(row.clicks || 0),
        cost: Number(row.cost || 0),
        conv: Number(row.conv || 0),
        value: Number(row.value || 0),
        imprShare: 0,
        lostBudget: 0,
        lostRank: 0,
        CTR: 0,
        CvR: 0,
        ROAS: 0,
        CPA: 0,
        AOV: 0,
        profit: 0,
      };
    });
  },

  H_DOW_7D: (data): HourlyData[] => {
    // Use the same transformer as H_DOW_30D
    return transformers.H_DOW_30D(data);
  },

  BIDDING: (data): CampaignSettings[] =>
    data.map((row) => ({
      campaign: String(row.campaign || row.Campaign || row.name || ""),
      campaignId: String(row.campaignId || row.CampaignId || row.id || ""),
      bidStrategy: String(row.bidStrategy || row.BidStrategy || row.type || ""),
      budget: String(row.budget || row.Budget || 0),
      status: String(row.status || row.Status || ""),
      labels: String(row.labels || row.Labels || ""),
      maxCpc: Number(row.maxCpc || row.MaxCPC || 0),
      rtbOptIn: Boolean(row.rtbOptIn || row.RTBOptIn || false),
      statusReasons: String(row.statusReasons || row.StatusReasons || ""),
      campaignType: String(row.campaignType || row.CampaignType || ""),
      targetCpa: Number(row.targetCpa || row.TargetCPA || 0),
      targetRoas: Number(
        row.targetRoas || row.TargetROAS || row.troas_target || 0,
      ),
      maxCpv: Number(row.maxCpv || row.MaxCPV || 0),
      excludedTypes: String(row.excludedTypes || row.ExcludedTypes || ""),
      includedTypes: String(row.includedTypes || row.IncludedTypes || ""),
      targetImprShare: Number(row.targetImprShare || row.TargetImprShare || 0),
      excludedPlacements: String(
        row.excludedPlacements || row.ExcludedPlacements || "",
      ),
      notes: String(row.notes || row.Notes || ""),
    })),

  QUALITY: (data): QualityData[] =>
    data.map((row) => {
      return {
        campaign: String(row.campaign || ""),
        adGroup: String(row.adGroup || ""),
        keywordText: String(row.keywordText || ""),
        score: Number(row.score || 0),
        matchType: String(row.matchType || ""),
        expCTR: String(row.expCTR || "AVERAGE"),
        adRelevance: String(row.adRelevance || "AVERAGE"),
        landingExp: String(row.landingExp || "AVERAGE"),
      };
    }),

  KEYWORD80: (data): KeywordData[] =>
    data.map((row) => {
      const metrics = transformBaseMetrics(row);
      return {
        campaign: String(row.campaign || row.Campaign || ""),
        adGroup: String(row.adGroup || row.adGroupName || ""),
        keywordText: String(row.keywordText || ""),
        keywordMatchType: String(row.matchType || row.keywordMatchType || ""),
        impr: metrics.impr,
        clicks: metrics.clicks,
        cost: metrics.cost,
        conv: metrics.conv,
        value: metrics.value,
        allConv: Number(row.allConv || 0),
        allValue: Number(row.allValue || 0),
      };
    }),

  SEARCH80: (data): SearchTermData[] =>
    data.map((row) => {
      const metrics = transformBaseMetrics(row);
      return {
        campaign: String(row.campaign || ""),
        searchTerm: String(row.searchTerm || row.SearchTerm || ""),
        searchMatchType: String(row.searchMatchType || row.matchtype || ""),
        date: String(
          row.date || row.Date || new Date().toISOString().split("T")[0],
        ),
        impr: metrics.impr,
        clicks: metrics.clicks,
        cost: metrics.cost,
        conv: metrics.conv,
        value: metrics.value,
        allConv: Number(row.allConv || 0),
        allValue: Number(row.allValue || 0),
        SearchTerm: String(row.searchTerm || row.SearchTerm || ""),
        impressions: metrics.impr,
        conversions: metrics.conv,
        convValue: metrics.value,
        CTR: metrics.CTR,
        CPC: metrics.cost / metrics.clicks || 0,
        CvR: metrics.CvR,
        ROAS: metrics.ROAS,
        CPA: metrics.CPA,
      };
    }),

  CAMP_CONV: (data: any[]): CampConvData[] =>
    data.map((row) => {
      const metrics = transformBaseMetrics(row);
      return {
        campaign: String(row.campaign || ""),
        value: metrics.value,
        conv: metrics.conv,
        allValue: Number(row.allValue || 0),
        allConv: Number(row.allConv || 0),
        networkType: String(row.networkType || ""),
        convCategory: String(row.convCategory || ""),
        date: String(row.date || ""),
        convAction: String(row.convAction || ""),
        destType: String(row.destType || ""),
      };
    }),

  CONV_ACTION: (data): ConvActionData[] =>
    data.map((row) => {
      const sanitized = sanitizeStrings(row);
      return {
        date: String(sanitized.date || sanitized.Date || ""),
        convName: String(
          sanitized.convName ||
            sanitized.conversionActionName ||
            sanitized.Conversionaction ||
            sanitized.conv_name ||
            "",
        ),
        convId: String(sanitized.convId || ""),
        status: String(
          sanitized.status || sanitized.convStatus || sanitized.Status || "",
        ),
        type: String(
          sanitized.type || sanitized.convType || sanitized.Type || "",
        ),
        allConv: Number(sanitized.allConv || 0),
        allValue: Number(sanitized.allValue || 0),
        countingType: String(sanitized.countingType || ""),
        includeInMetric:
          String(sanitized.includeInMetric).toLowerCase() === "true",
        primaryGoal: String(sanitized.primaryGoal).toLowerCase() === "true",
      };
    }),

  KEYWORD80_PREV: (data): KeywordData[] =>
    data.map((row) => {
      const sanitized = sanitizeStrings(row);
      const metrics = transformBaseMetrics(sanitized);
      return {
        campaign: String(sanitized.campaign || sanitized.Campaign || ""),
        adGroup: String(
          sanitized.adGroup || sanitized.AdGroup || sanitized.adGroupName || "",
        ),
        keywordText: String(
          sanitized.keywordText || sanitized.Keyword || sanitized.keyword || "",
        ),
        keywordMatchType: String(
          sanitized.keywordMatchType ||
            sanitized.MatchType ||
            sanitized.matchType ||
            "",
        ),
        ...metrics,
        allConv: Number(sanitized.allConv || 0),
        allValue: Number(sanitized.allValue || 0),
      };
    }),

  CAMPAIGN_IDS_SPEND: (data): CampaignIdsWithSpendData[] =>
    data.map((row) => {
      const sanitized = sanitizeStrings(row);
      return {
        campaignId: String(sanitized.campaignId || sanitized.campId || ""),
        cost: Number(sanitized.cost || sanitized.Cost || 0),
      };
    }),

  CHANGES: (data): ChangesData[] =>
    data.map((row) => {
      const sanitized = sanitizeStrings(row);
      return {
        changeDateTime: String(
          sanitized.changeDateTime ||
            sanitized.date ||
            sanitized.changeDate ||
            "",
        ),
        changeType: String(
          sanitized.changeType || sanitized.type || sanitized.changetype || "",
        ),
        changedFields: String(
          sanitized.changedFields || sanitized.fields || "",
        ),
        userEmail: String(sanitized.userEmail || sanitized.user || ""),
        campaignName: String(
          sanitized.campaignName || sanitized.campaign || "",
        ),
        campaignId: String(sanitized.campaignId || sanitized.campId || ""),
        changeResourceName: String(
          sanitized.changeResourceName ||
            sanitized.resource ||
            sanitized.changeResource ||
            "",
        ),
        operation: String(sanitized.operation || sanitized.op || ""),
        biddingStrategy: String(sanitized.biddingStrategy || ""),
        targetROAS: String(sanitized.targetROAS || sanitized.troas || ""),
        targetCPA: String(sanitized.targetCPA || sanitized.tcpa || ""),
        directTargetROAS: String(sanitized.directTargetROAS || ""),
        channelType: String(sanitized.channelType || ""),
        channelSubType: String(sanitized.channelSubType || ""),
        servingStatus: String(sanitized.servingStatus || ""),
        primaryStatus: String(sanitized.primaryStatus || ""),
        primaryReasons: String(sanitized.primaryReasons || ""),
        status: String(sanitized.status || ""),
        tcpaCeiling: String(sanitized.tcpaCeiling || ""),
        tcpaFloor: String(sanitized.tcpaFloor || ""),
        tcpaTarget: String(sanitized.tcpaTarget || ""),
        troasCeiling: String(sanitized.troasCeiling || ""),
        troasFloor: String(sanitized.troasFloor || ""),
        troasTarget: String(sanitized.troasTarget || ""),
        tsCeiling: String(sanitized.tsCeiling || ""),
        tsTarget: String(sanitized.tsTarget || ""),
        budget: String(sanitized.budget || ""),
        targetSearchNetwork: String(sanitized.targetSearchNetwork || ""),
        targetPartnerSearchNetwork: String(
          sanitized.targetPartnerSearchNetwork || "",
        ),
        targetGoogleTvNetwork: String(sanitized.targetGoogleTvNetwork || ""),
        targetGoogleSearch: String(sanitized.targetGoogleSearch || ""),
        targetContentNetwork: String(sanitized.targetContentNetwork || ""),
        targetYoutube: String(sanitized.targetYoutube || ""),
        optimizationGoalTypes: String(sanitized.optimizationGoalTypes || ""),
      };
    }),

  ACCT_OVERVIEW: (data): AcctOverviewData[] =>
    data.map((row) => {
      const sanitized = sanitizeStrings(row);
      const impressions = Number(sanitized.impressions || sanitized.impr || 0);
      const clicks = Number(sanitized.clicks || 0);
      const cost = Number(sanitized.cost || 0);
      const conversions = Number(sanitized.conversions || sanitized.conv || 0);
      const conversionValue = Number(
        sanitized.conversionValue || sanitized.value || 0,
      );
      const allConversions = Number(
        sanitized.allConversions || sanitized.allConv || 0,
      );
      const allConversionValue = Number(
        sanitized.allConversionValue || sanitized.allValue || 0,
      );
      return {
        date: String(sanitized.date || sanitized.Date || ""),
        impressions,
        clicks,
        cost,
        conversions,
        conversionValue,
        allConversions,
        allConversionValue,
        ctr: impressions ? (clicks / impressions) * 100 : 0,
        cpc: clicks ? cost / clicks : 0,
        convRate: clicks ? (conversions / clicks) * 100 : 0,
        roas: cost ? conversionValue / cost : 0,
      };
    }),

  CONV_TRENDS: (data): ConvTrendsData[] =>
    data.map((row) => {
      const sanitized = sanitizeStrings(row);
      return {
        date: String(sanitized.date || sanitized.Date || ""),
        campaignName: String(
          sanitized.campaignName ||
            sanitized["Campaign Name"] ||
            sanitized.campaign ||
            sanitized["campaign.name"] ||
            sanitized.CampaignName ||
            sanitized.Campaign ||
            "",
        ),
        campaignId: String(
          sanitized.campaignId ||
            sanitized["Campaign ID"] ||
            sanitized["campaign.id"] ||
            sanitized.campId ||
            sanitized.CampaignId ||
            "",
        ),
        conversionActionName: String(
          sanitized.conversionActionName ||
            sanitized["Conversion Action"] ||
            sanitized.conversionAction ||
            sanitized.convActionName ||
            sanitized.ConversionActionName ||
            "",
        ),
        conversionCategory: String(
          sanitized.conversionCategory ||
            sanitized.Category ||
            sanitized.conversionActionCategory ||
            sanitized.convActionCat ||
            sanitized.ConversionCategory ||
            "",
        ),
        conversions: Number(
          sanitized.conversions || sanitized.Conversions || sanitized.conv || 0,
        ),
        conversionValue: Number(
          sanitized.conversionValue ||
            sanitized["Conv. Value"] ||
            sanitized.ConversionValue ||
            sanitized.value ||
            0,
        ),
        allConversions: Number(
          sanitized.allConversions ||
            sanitized["All Conversions"] ||
            sanitized.AllConversions ||
            sanitized.allConv ||
            0,
        ),
        allConversionValue: Number(
          sanitized.allConversionValue ||
            sanitized["All Conv. Value"] ||
            sanitized.AllConversionValue ||
            sanitized.allValue ||
            0,
        ),
      };
    }),

  ST_INSIGHTS: (data): SearchTermInsightRow[] =>
    data.map((row, index) => {
      const sanitized = sanitizeStrings(row);
      const transformedRow = {
        insightType: String(
          sanitized["Insight Type"] || sanitized.insightType || "",
        ),
        searchTerm: String(
          sanitized["Search Term"] || sanitized.searchTerm || "",
        ),
        impressionTrend: String(
          sanitized["Impression Trend"] || sanitized.impressionTrend || "",
        ),
        currentImprPerDay: String(
          sanitized["Current Impr/Day"] || sanitized.currentImprPerDay || "",
        ),
        currentClicksPerDay: String(
          sanitized["Current Clicks/Day"] ||
            sanitized.currentClicksPerDay ||
            "",
        ),
        currentCostPerDay: String(
          sanitized["Current Cost/Day"] || sanitized.currentCostPerDay || "",
        ),
        currentConvPerDay: String(
          sanitized["Current Conv/Day"] || sanitized.currentConvPerDay || "",
        ),
        previousImprPerDay: String(
          sanitized["Previous Impr/Day"] || sanitized.previousImprPerDay || "",
        ),
        previousConvPerDay: String(
          sanitized["Previous Conv/Day"] || sanitized.previousConvPerDay || "",
        ),
        firstAppearance: String(
          sanitized["First Appearance"] ||
            sanitized.firstAppearance ||
            sanitized.FirstAppearance ||
            "",
        ),
        lastAppearance: String(
          sanitized["Last Appearance"] || sanitized.lastAppearance || "",
        ),
        coverage: String(sanitized["Coverage"] || sanitized.coverage || ""),
        gapCount: String(sanitized["Gap Count"] || sanitized.gapCount || ""),
        avgImprOnActive: String(
          sanitized["Avg Impr On Active"] || sanitized.avgImprOnActive || "",
        ),
        recommendation: String(
          sanitized["Recommendation"] || sanitized.recommendation || "",
        ),
      } as SearchTermInsightRow;
      return transformedRow;
    }),

  KW_CPC: (data): KwCpcData[] =>
    data.map((row) => {
      const sanitized = sanitizeStrings(row);
      return {
        campaignName: String(
          sanitized.campaignName || sanitized.Campaign || "",
        ),
        adGroupName: String(sanitized.adGroupName || sanitized.AdGroup || ""),
        keywordText: String(sanitized.keywordText || sanitized.Keyword || ""),
        keywordMatchType: String(
          sanitized.keywordMatchType || sanitized.MatchType || "",
        ),
        impr: Number(sanitized.impr || sanitized.Impressions || 0),
        clicks: Number(sanitized.clicks || sanitized.Clicks || 0),
        cost: Number(sanitized.cost || sanitized.Cost || 0),
        avgCpc: Number(sanitized.avgCpc || sanitized["Avg. CPC"] || 0),
      };
    }),

  ASSETS: (data): AssetsData[] =>
    data.map((row) => {
      const sanitized = sanitizeStrings(row);
      return {
        assetId: String(sanitized.assetId || ""),
        assetName: String(sanitized.assetName || ""),
        assetType: String(sanitized.assetType || ""),
        assetText: String(sanitized.assetText || ""),
        assetImageUrl: String(sanitized.assetImageUrl || ""),
        assetYoutubeVideoId: String(sanitized.assetYoutubeVideoId || ""),
        assetYoutubeVideoTitle: String(sanitized.assetYoutubeVideoTitle || ""),
      };
    }),

  PMAX_ASSETS: (data): PMaxAssetsData[] =>
    data.map((row) => {
      const sanitized = sanitizeStrings(row);
      const cost = Number(sanitized.cost || 0);
      const clicks = Number(sanitized.clicks || 0);
      const conversions = Number(sanitized.conversions || 0);
      const conversionValue = Number(sanitized.conversionValue || 0);
      const impressions = Number(sanitized.impressions || 0);
      return {
        date: String(sanitized.date || ""),
        campaignId: String(sanitized.campaignId || ""),
        campaignName: String(sanitized.campaignName || ""),
        assetId: String(sanitized.assetId || ""),
        assetFieldType: String(sanitized.assetFieldType || ""),
        assetSource: String(sanitized.assetSource || ""),
        assetGroupName: String(sanitized.assetGroupName || ""),
        assetGroupId: String(sanitized.assetGroupId || ""),
        impressions,
        clicks,
        cost,
        conversions,
        conversionValue,
        videoViews: Number(sanitized.videoViews || 0),
        ctr: impressions ? (clicks / impressions) * 100 : 0,
        cpc: clicks ? cost / clicks : 0,
        cpa: conversions ? cost / conversions : 0,
        roas: cost ? conversionValue / cost : 0,
        cvr: clicks ? (conversions / clicks) * 100 : 0,
      };
    }),

  AD_ASSETS: (data): AdAssetsData[] =>
    data.map((row) => {
      const sanitized = sanitizeStrings(row);
      return {
        segDate: String(
          sanitized.segDate || sanitized.seg_date || sanitized.date || "",
        ),
        campId: String(sanitized.campId || sanitized.camp_id || ""),
        campaignName: String(
          sanitized.campaignName ||
            sanitized.campaign_name ||
            sanitized.campaign ||
            "",
        ),
        campType: String(
          sanitized.campType ||
            sanitized.campaignType ||
            sanitized.camp_type ||
            sanitized.channel ||
            "",
        ),
        adGroupId: String(
          sanitized.adGroupId ||
            sanitized.ad_group_id ||
            sanitized.adgroupid ||
            "",
        ),
        adGroupName: String(sanitized.adGroupName || ""),
        adId: String(sanitized.adId || ""),
        assetId: String(sanitized.assetId || ""),
        assetSource: String(
          sanitized.assetSource || sanitized.asset_source || "",
        ),
        impr: Number(sanitized.impr || 0),
        clicks: Number(sanitized.clicks || 0),
        cost: Number(sanitized.cost || 0),
        conv: Number(sanitized.conv || 0),
        value: Number(sanitized.value || 0),
        allConv: Number(sanitized.allConv || 0),
        allValue: Number(sanitized.allValue || 0),
      };
    }),

  CAMP_ASSETS: (data): CampAssetsData[] =>
    data.map((row) => {
      const sanitized = sanitizeStrings(row);
      return {
        segDate: String(
          sanitized.segDate || sanitized.date || sanitized.Date || "",
        ),
        campId: String(
          sanitized.campId ||
            sanitized.campaignId ||
            sanitized.camp_id ||
            sanitized.CampaignId ||
            "",
        ),
        campaignName: String(
          sanitized.campaignName ||
            sanitized.campaign ||
            sanitized.Campaign ||
            "",
        ),
        campType: String(
          sanitized.campType ||
            sanitized.channel ||
            sanitized.campaignType ||
            sanitized.Channel ||
            "",
        ),
        assetId: String(sanitized.assetId || sanitized.AssetId || ""),
        assetFieldType: String(
          sanitized.assetFieldType || sanitized.AssetFieldType || "",
        ),
        assetSource: String(
          sanitized.assetSource || sanitized.AssetSource || "",
        ),
        assetStatus: String(
          sanitized.assetStatus || sanitized.status || sanitized.Status || "",
        ),
        campaignStatus: String(
          sanitized.campaignStatus || sanitized.CampaignStatus || "",
        ),
        impr: Number(sanitized.impr || 0),
        clicks: Number(sanitized.clicks || 0),
        cost: Number(sanitized.cost || 0),
        conv: Number(sanitized.conv || 0),
        value: Number(sanitized.value || 0),
        allConv: Number(sanitized.allConv || 0),
        allValue: Number(sanitized.allValue || 0),
      };
    }),

  AG_ASSETS: (data): AgAssetsData[] =>
    data.map((row) => {
      const sanitized = sanitizeStrings(row);
      return {
        segDate: String(sanitized.segDate || sanitized.date || ""),
        campId: String(
          sanitized.campId || sanitized.camp_id || sanitized.campaignId || "",
        ),
        campaignName: String(
          sanitized.campaignName || sanitized.campaign || "",
        ),
        campaignStatus: String(
          sanitized.campaignStatus || sanitized.status || "",
        ),
        campType: String(sanitized.campType || sanitized.channel || ""),
        adGroupId: String(
          sanitized.adGroupId ||
            sanitized.adgroupid ||
            sanitized.ad_group_id ||
            "",
        ),
        adGroupName: String(sanitized.adGroupName || ""),
        adGroupStatus: String(
          sanitized.adGroupStatus ||
            sanitized.adGroupStatus ||
            sanitized.adgroupstatus ||
            "",
        ),
        adId: String(sanitized.adId || ""),
        impr: Number(sanitized.impr || 0),
        clicks: Number(sanitized.clicks || 0),
        cost: Number(sanitized.cost || 0),
        conv: Number(sanitized.conv || 0),
        value: Number(sanitized.value || 0),
        allConv: Number(sanitized.allConv || 0),
        allValue: Number(sanitized.allValue || 0),
      };
    }),

  GEO_PERF: (data): GeoPerfData[] =>
    data.map((row) => {
      const sanitized = sanitizeStrings(row);
      return {
        campaignName: String(
          sanitized.campaignName || sanitized.Campaign || "",
        ),
        countryName: String(
          sanitized.countryName ||
            sanitized.Country ||
            sanitized.CountryName ||
            "",
        ),
        locationType: String(
          sanitized.locationType ||
            sanitized.LocationType ||
            sanitized["Location Type"] ||
            "",
        ),
        resolvedLocation: String(
          sanitized.resolvedLocation ||
            sanitized.ResolvedLocation ||
            sanitized.Region ||
            sanitized.region ||
            sanitized.State ||
            sanitized.state ||
            sanitized.County ||
            sanitized.county ||
            sanitized.Province ||
            sanitized.province ||
            sanitized.Canton ||
            sanitized.canton ||
            "",
        ),
        impr: Number(sanitized.impr || sanitized.Impressions || 0),
        clicks: Number(sanitized.clicks || sanitized.Clicks || 0),
        cost: Number(sanitized.cost || sanitized.Cost || 0),
        conv: Number(sanitized.conv || sanitized.Conversions || 0),
        value: Number(
          sanitized.value ||
            sanitized["Conv. Value"] ||
            sanitized.ConvValue ||
            0,
        ),
      };
    }),

  LPAGE_PERF: (data): LPagePerfData[] =>
    data.map((row) => {
      const sanitized = sanitizeStrings(row);
      return {
        segDate: String(
          sanitized.segDate || sanitized.Date || sanitized.date || "",
        ),
        campaignName: String(
          sanitized.campaignName ||
            sanitized.Campaign ||
            sanitized.campaign ||
            "",
        ),
        adGroupName: String(
          sanitized.adGroupName || sanitized.AdGroupName || "",
        ),
        finalUrl: String(sanitized.finalUrl || sanitized.FinalUrl || ""),
        impr: Number(sanitized.impr || sanitized.Impressions || 0),
        clicks: Number(sanitized.clicks || sanitized.Clicks || 0),
        cost: Number(sanitized.cost || sanitized.Cost || 0),
        conv: Number(sanitized.conv || sanitized.Conversions || 0),
        value: Number(
          sanitized.value ||
            sanitized.ConvValue ||
            sanitized["Conv. Value"] ||
            0,
        ),
        allConv: Number(sanitized.allConv || sanitized.AllConversions || 0),
        allValue: Number(
          sanitized.allValue ||
            sanitized.AllConvValue ||
            sanitized["All Conv. Value"] ||
            0,
        ),
      };
    }),

  CAMPAIGN_BUDGETS: (data): CampaignBudgetData[] =>
    data.map((row) => ({
      campaignName: String(
        row["Campaign Name"] ||
          row.campaignName ||
          row.campaign ||
          row.Campaign ||
          "",
      ),
      campaignId: String(row["Campaign ID"] || row.campaignId || ""),
      budgetId: String(row["Budget ID"] || row.budgetId || ""),
      budgetName: String(row["Budget Name"] || row.budgetName || ""),
      budgetAmount: Number(row["Budget Amount"] || row.budgetAmount || 0),
      deliveryMethod: String(
        row["Delivery Method"] || row.deliveryMethod || "",
      ),
      sharedBudget: Boolean(
        row["Shared Budget"] === "TRUE" ||
          row.sharedBudget === true ||
          row.sharedBudget === "true",
      ),
      budgetStatus: String(row["Budget Status"] || row.budgetStatus || ""),
    })),

  WEEKLY_PERFORMANCE: (data): WeeklyPerformanceData[] =>
    data.map((row) => {
      const metrics = transformBaseMetrics(row);
      return {
        week: String(row.week || row.Week || ""),
        campaignName: String(
          row.campaignName ||
            row.CampaignName ||
            row["Campaign Name"] ||
            row.campaign ||
            row.Campaign ||
            "",
        ),
        campaignId: String(
          row.campaignId ||
            row.CampaignId ||
            row["Campaign ID"] ||
            row.campaign_id ||
            "",
        ),
        days: Number(row.days || row.Days || 7), // Default to 7 if not provided
        impr: metrics.impr,
        clicks: metrics.clicks,
        cost: metrics.cost,
        conv: metrics.conv,
        value: metrics.value,
        ctr: metrics.CTR,
        cpc: metrics.clicks > 0 ? metrics.cost / metrics.clicks : 0,
        cpa: metrics.CPA,
        roas: metrics.ROAS,
        cvr: metrics.CvR,
      };
    }),

  SMART_BIDDING_CHANGES: (data): SmartBiddingChangesData[] =>
    data.map((row) => {
      const sanitized = sanitizeStrings(row);
      return {
        changeDateTime: String(
          sanitized["Change Date Time"] ||
            sanitized.changeDateTime ||
            sanitized.change_date_time ||
            sanitized.ChangeDateTime ||
            "",
        ),
        changeType: String(
          sanitized["Change Type"] ||
            sanitized.changeType ||
            sanitized.change_event_type ||
            sanitized.ChangeEvent ||
            "",
        ),
        changedFields: String(
          sanitized["Changed Fields"] ||
            sanitized.changedFields ||
            sanitized.changed_fields ||
            sanitized.ChangedFields ||
            "",
        ),
        userEmail: String(
          sanitized["User Email"] ||
            sanitized.userEmail ||
            sanitized.user_email ||
            sanitized.UserEmail ||
            "",
        ),
        campaignName: String(
          sanitized["Campaign Name"] ||
            sanitized.campaignName ||
            sanitized.campaign ||
            sanitized.CampaignName ||
            "",
        ),
        campaignId: String(
          sanitized["Campaign ID"] ||
            sanitized.campaignId ||
            sanitized.campaign_id ||
            sanitized.CampaignId ||
            "",
        ),
      };
    }),

  DG_NETWORK_PERF: (data): DgNetworkPerfData[] =>
    data.map((row) => ({
      Date: String(row.Date || row.date || ""),
      Campaign: String(row.Campaign || row.campaign || ""),
      CampaignID: String(row.CampaignID || row.campaignId || ""),
      TotalCost: Number(row.TotalCost || row.totalCost || 0),
      TotalConv: Number(row.TotalConv || row.totalConv || 0),
      YouTubeCost: Number(row.YouTubeCost || row.youTubeCost || 0),
      DiscoverCost: Number(row.DiscoverCost || row.discoverCost || 0),
      GmailCost: Number(row.GmailCost || row.gmailCost || 0),
      YouTubeConv: Number(row.YouTubeConv || row.youTubeConv || 0),
      DiscoverConv: Number(row.DiscoverConv || row.discoverConv || 0),
      GmailConv: Number(row.GmailConv || row.gmailConv || 0),
      YouTubeCostPercent: Number(
        row.YouTubeCostPercent || row.youTubeCostPercent || 0,
      ),
      DiscoverCostPercent: Number(
        row.DiscoverCostPercent || row.discoverCostPercent || 0,
      ),
      GmailCostPercent: Number(
        row.GmailCostPercent || row.gmailCostPercent || 0,
      ),
      YouTubeConvPercent: Number(
        row.YouTubeConvPercent || row.youTubeConvPercent || 0,
      ),
      DiscoverConvPercent: Number(
        row.DiscoverConvPercent || row.discoverConvPercent || 0,
      ),
      GmailConvPercent: Number(
        row.GmailConvPercent || row.gmailConvPercent || 0,
      ),
    })),

  DG_ADS: (data): DgAdsData[] =>
    data.map((row) => {
      const sanitized = sanitizeStrings(row);
      return {
        adId: String(sanitized.adId || sanitized.AdId || ""),
        adName: String(sanitized.adName || sanitized.AdName || ""),
        adType: String(sanitized.adType || sanitized.AdType || ""),
        finalUrls: String(sanitized.finalUrls || sanitized.FinalUrls || ""),
        adStatus: String(sanitized.adStatus || sanitized.AdStatus || ""),
        adGroupId: String(
          sanitized.adGroupId ||
            sanitized.adgroupid ||
            sanitized.AdGroupId ||
            "",
        ),
        adGroupName: String(
          sanitized.adGroupName || sanitized.AdGroupName || "",
        ),
        campaignId: String(sanitized.campaignId || sanitized.CampaignId || ""),
        campaignName: String(
          sanitized.campaignName ||
            sanitized.campaign ||
            sanitized.Campaign ||
            "",
        ),
        impr: Number(sanitized.impr || 0),
        clicks: Number(sanitized.clicks || 0),
        cost: Number(sanitized.cost || 0),
        conv: Number(sanitized.conv || 0),
        value: Number(sanitized.value || 0),
        allConv: Number(sanitized.allConv || 0),
        allValue: Number(sanitized.allValue || 0),
      };
    }),

  DG_ASSETS: (data): DgAssetsData[] =>
    data.map((row) => {
      const sanitized = sanitizeStrings(row);
      return {
        assetId: String(sanitized.assetId || sanitized.AssetId || ""),
        assetName: String(sanitized.assetName || sanitized.AssetName || ""),
        assetSource: String(
          sanitized.assetSource || sanitized.AssetSource || "",
        ),
        assetType: String(sanitized.assetType || sanitized.AssetType || ""),
        assetContent: String(
          sanitized.assetContent || sanitized.AssetContent || "",
        ),
        youtubeVideoId: String(
          sanitized.youtubeVideoId || sanitized.YoutubeVideoId || "",
        ),
        youtubeVideoTitle: String(
          sanitized.youtubeVideoTitle || sanitized.YoutubeVideoTitle || "",
        ),
        callToAction: String(
          sanitized.callToAction || sanitized.CallToAction || "",
        ),
        adId: String(sanitized.adId || sanitized.AdId || ""),
        adName: String(sanitized.adName || sanitized.AdName || ""),
        adType: String(sanitized.adType || sanitized.AdType || ""),
        adGroupName: String(
          sanitized.adGroupName || sanitized.AdGroupName || "",
        ),
        campaign: String(sanitized.campaign || sanitized.Campaign || ""),
        impr: Number(sanitized.impr || sanitized.impressions || 0),
        clicks: Number(sanitized.clicks || 0),
        cost: Number(sanitized.cost || 0),
        conv: Number(sanitized.conv || sanitized.conversions || 0),
        value: Number(sanitized.value || sanitized.conversionValue || 0),
      };
    }),

  ON_PERFORMANCE: (data): OnPerformanceData[] =>
    data.map((row) => {
      const sanitized = sanitizeStrings(row);
      return {
        Campaign: String(sanitized.Campaign || sanitized.campaignName || ""),
        ImprYesterday: Number(sanitized.ImprYesterday || 0),
        ImprAvg13W: Number(sanitized.ImprAvg13W || 0),
        ImprDrop13W: Number(sanitized.ImprDrop13W || 0),
        ImprLevel13W: String(sanitized.ImprLevel13W || ""),
        ImprAvg4W: Number(sanitized.ImprAvg4W || 0),
        ImprDrop4W: Number(sanitized.ImprDrop4W || 0),
        ImprLevel4W: String(sanitized.ImprLevel4W || ""),
        CostYesterday: Number(sanitized.CostYesterday || 0),
        CostAvg13W: Number(sanitized.CostAvg13W || 0),
        CostDrop13W: Number(sanitized.CostDrop13W || 0),
        CostLevel13W: String(
          sanitized.CostLevel13W || sanitized.costLevel13W || "",
        ),
        CostAvg4W: Number(sanitized.CostAvg4W || 0),
        CostDrop4W: Number(sanitized.CostDrop4W || 0),
        CostLevel4W: String(
          sanitized.CostLevel4W || sanitized.costLevel4W || "",
        ),
        ConvYesterday: Number(sanitized.ConvYesterday || 0),
        ConvAvg13W: Number(sanitized.ConvAvg13W || 0),
        ConvDrop13W: Number(sanitized.ConvDrop13W || 0),
        ConvLevel13W: String(sanitized.ConvLevel13W || ""),
        ConvAvg4W: Number(sanitized.ConvAvg4W || 0),
        ConvDrop4W: Number(sanitized.ConvDrop4W || 0),
        ConvLevel4W: String(sanitized.ConvLevel4W || ""),
      };
    }),

  ON_SETTINGS: (data): OnSettingsData[] =>
    data.map((row) => {
      const sanitized = sanitizeStrings(row);
      return {
        campaign: String(
          sanitized.campaign ||
            sanitized.Campaign ||
            sanitized.campaignName ||
            "",
        ),
        campaignId: String(
          sanitized.campaignId ||
            sanitized.CampaignId ||
            sanitized.campaign_id ||
            "",
        ),
        status: String(sanitized.status || sanitized.Status || ""),
        channel: String(sanitized.channel || sanitized.Channel || ""),
        targetContentNetwork: Boolean(sanitized.targetContentNetwork || false),
        networkType: String(
          sanitized.networkType || sanitized.NetworkType || "",
        ),
        geoTargetTypePos:
          sanitized.geoTargetTypePos ||
          sanitized.GeoTargetTypePos ||
          sanitized.geo_target_type_pos
            ? String(
                sanitized.geoTargetTypePos ||
                  sanitized.GeoTargetTypePos ||
                  sanitized.geo_target_type_pos,
              )
            : undefined,
        impr: Number(sanitized.impr || sanitized.Impressions || 0),
        clicks: Number(sanitized.clicks || sanitized.Clicks || 0),
        cost: Number(sanitized.cost || sanitized.Cost || 0),
        conv: Number(sanitized.conv || sanitized.Conversions || 0),
        value: Number(sanitized.value || sanitized.ConvValue || 0),
        tcpaTarget:
          sanitized.tcpaTarget || sanitized.TcpaTarget || sanitized.tcpa_target
            ? Number(
                sanitized.tcpaTarget ||
                  sanitized.TcpaTarget ||
                  sanitized.tcpa_target,
              )
            : undefined,
        troasTarget:
          sanitized.troasTarget ||
          sanitized.TroasTarget ||
          sanitized.troas_target
            ? Number(
                sanitized.troasTarget ||
                  sanitized.TroasTarget ||
                  sanitized.troas_target,
              )
            : undefined,
        lostBudget:
          sanitized.lostBudget || sanitized.LostBudget || sanitized.lost_budget
            ? Number(
                sanitized.lostBudget ||
                  sanitized.LostBudget ||
                  sanitized.lost_budget,
              )
            : undefined,
      };
    }),

  ON_DISAPPROVED: (data): OnDisapprovedData[] =>
    data.map((row) => {
      const sanitized = sanitizeStrings(row);
      return {
        campaign: String(sanitized.campaign || sanitized.campaignName || ""),
        adGroupName: String(sanitized.adGroupName || ""),
        adId: String(sanitized.adId || ""),
        approvalStatus: String(
          sanitized.approvalStatus || sanitized.policySummary || "",
        ),
        policyTopics: String(sanitized.policyTopics || "[]"),
      };
    }),

  ON_EMPTY_AD_GROUPS: (data): OnEmptyAdGroupsData[] =>
    data.map((row) => {
      const sanitized = sanitizeStrings(row);
      return {
        campaign: String(
          sanitized.campaign ||
            sanitized.Campaign ||
            sanitized.campaignName ||
            "",
        ),
        campaignId: String(sanitized.campaignId || sanitized.CampaignId || ""),
        status: String(sanitized.status || sanitized.Status || ""),
        adGroupName: String(
          sanitized.adGroupName || sanitized.AdGroupName || "",
        ),
        adgroupid: String(
          sanitized.adgroupid ||
            sanitized.adGroupId ||
            sanitized.AdGroupId ||
            "",
        ),
        adgroupstatus: String(
          sanitized.adgroupstatus ||
            sanitized.adGroupStatus ||
            sanitized.AdGroupStatus ||
            "",
        ),
      };
    }),

  // Intent Mapper tabs - passthrough transformers (data used directly with camelCase keys)
  IM_META: (data): any[] => data,
  IM_CAMPAIGNS: (data): any[] => data,
  IM_KEYWORDS: (data): any[] => data,
  IM_SEARCH_TERMS: (data): any[] => data,
  IM_PRODUCTS: (data): any[] => data,
  IM_ASSET_GROUPS: (data): any[] => data,
  IM_PMAX_CATEGORIES: (data): any[] => data,
  IM_PMAX_SEARCH_TERMS: (data): any[] => data,
  IM_ADS: (data): any[] => data,
  IM_AUDIENCES: (data): any[] => data,

  // PMax Network - passthrough
  PMAX_NETWORK: (data): any[] => data,

  // RSA Assets - passthrough
  RSA_ASSETS: (data): any[] => data,
};

export const createEmptyStorageData = (): Omit<StorageData, "timestamp"> => ({
  dexieId: SAMPLE_ACCOUNT_ID,
  accountCID: SAMPLE_ACCOUNT_ID,
  records: {},
  [SHEET_TABS.ACCOUNT]: [],
  [SHEET_TABS.DAILY]: [],
  [SHEET_TABS.HOURLY]: [],
  [SHEET_TABS.HOURLY_YEST]: [],
  [SHEET_TABS.THIRTY_DAYS]: [],
  [SHEET_TABS.P_THIRTY_DAYS]: [],
  [SHEET_TABS.SEVEN_DAYS]: [],
  [SHEET_TABS.P_SEVEN_DAYS]: [],
  [SHEET_TABS.SETTINGS]: [],
  [SHEET_TABS.PRODUCTS]: [],
  [SHEET_TABS.PMAX_PERF]: [],
  [SHEET_TABS.PMAX_NETWORK]: [],
  [SHEET_TABS.H_CAMP_30D]: [],
  [SHEET_TABS.H_CAMP_7D]: [],
  [SHEET_TABS.H_DOW_30D]: [],
  [SHEET_TABS.H_DOW_7D]: [],
  [SHEET_TABS.BIDDING]: [],
  [SHEET_TABS.QUALITY]: [],
  [SHEET_TABS.PLACEMENTS]: [],
  [SHEET_TABS.KEYWORD80]: [],
  [SHEET_TABS.KEYWORD80_PREV]: [],
  [SHEET_TABS.SEARCH80]: [],
  [SHEET_TABS.CAMP_CONV]: [],
  [SHEET_TABS.CONV_ACTION]: [],
  [SHEET_TABS.CAMPAIGN_IDS_SPEND]: [],
  [SHEET_TABS.CHANGES]: [],
  [SHEET_TABS.ACCT_OVERVIEW]: [],
  [SHEET_TABS.CONV_TRENDS]: [],
  [SHEET_TABS.ST_INSIGHTS]: [],
  [SHEET_TABS.KW_CPC]: [],
  [SHEET_TABS.ASSETS]: [],
  [SHEET_TABS.PMAX_ASSETS]: [],
  [SHEET_TABS.AD_ASSETS]: [],
  [SHEET_TABS.CAMP_ASSETS]: [],
  [SHEET_TABS.AG_ASSETS]: [],
  [SHEET_TABS.GEO_PERF]: [],
  [SHEET_TABS.LPAGE_PERF]: [],
  [SHEET_TABS.CAMPAIGN_BUDGETS]: [],
  [SHEET_TABS.WEEKLY_PERFORMANCE]: [],
  [SHEET_TABS.SMART_BIDDING_CHANGES]: [],
  [SHEET_TABS.DG_NETWORK_PERF]: [],
  [SHEET_TABS.DG_ADS]: [],
  [SHEET_TABS.DG_ASSETS]: [],
  [SHEET_TABS.ON_PERFORMANCE]: [],
  [SHEET_TABS.ON_SETTINGS]: [],
  [SHEET_TABS.ON_DISAPPROVED]: [],
  [SHEET_TABS.ON_EMPTY_AD_GROUPS]: [],
  [SHEET_TABS.IM_META]: [],
  [SHEET_TABS.IM_CAMPAIGNS]: [],
  [SHEET_TABS.IM_KEYWORDS]: [],
  [SHEET_TABS.IM_SEARCH_TERMS]: [],
  [SHEET_TABS.IM_PRODUCTS]: [],
  [SHEET_TABS.IM_ASSET_GROUPS]: [],
  [SHEET_TABS.IM_PMAX_CATEGORIES]: [],
  [SHEET_TABS.IM_PMAX_SEARCH_TERMS]: [],
  [SHEET_TABS.IM_ADS]: [],
  [SHEET_TABS.IM_AUDIENCES]: [],
  [SHEET_TABS.RSA_ASSETS]: [],
});

// Then update the loadSampleData function to properly access transformers
export async function loadSampleData(): Promise<StorageData> {
  const timestamp = new Date().toISOString();
  const emptyData = createEmptyStorageData();

  // Clear browser cache for sample data files
  if ("caches" in window) {
    try {
      const cacheNames = await caches.keys();
      for (const cacheName of cacheNames) {
        const cache = await caches.open(cacheName);
        const requests = await cache.keys();
        for (const request of requests) {
          if (request.url.includes("/sample-data/")) {
            await cache.delete(request);
          }
        }
      }
    } catch (err) {
      // Cache clearing failed, continue anyway
    }
  }

  try {
    const results = await Promise.all(
      (Object.keys(SHEET_TABS) as Array<keyof typeof SHEET_TABS>).map(
        async (tab) => {
          try {
            if (!(tab in transformers)) {
              console.warn(`No transformer found for tab: ${tab}. Skipping.`);
              return { tab, data: [] };
            }
            const rawData = await loadCsvFile(tab);
            const transformedData = transformers[tab](rawData);
            return { tab, data: transformedData };
          } catch (err) {
            console.warn(`Failed to load ${tab} data:`, err);
            return { tab, data: [] };
          }
        },
      ),
    );

    const dataMap = new Map(results.map((r) => [r.tab, r.data]));

    const qualityData = (dataMap.get("QUALITY") as QualityData[]) || [];
    const keywordData = (dataMap.get("KEYWORD80") as KeywordData[]) || [];

    const qualityIndex = new Map<string, QualityData>();
    qualityData.forEach((q) => {
      const key = `${q.campaign}@@@@@${q.adGroup}@@@@@${q.keywordText}@@@@@${q.matchType}`;
      qualityIndex.set(key, q);
    });

    const joinedKeywordData = keywordData.map((kw) => {
      const key = `${kw.campaign}@@@@@${kw.adGroup}@@@@@${kw.keywordText}@@@@@${kw.keywordMatchType}`;
      const qualityInfo = qualityIndex.get(key);
      return {
        ...kw,
        score: qualityInfo?.score ?? null,
        expCTR: qualityInfo?.expCTR ?? null,
        adRelevance: qualityInfo?.adRelevance ?? null,
        landingExp: qualityInfo?.landingExp ?? null,
      };
    });

    dataMap.set("KEYWORD80", joinedKeywordData);

    const result: StorageData = {
      ...emptyData,
      timestamp,
      dexieId: SAMPLE_ACCOUNT_ID,
    };

    // Populate data from results
    results.forEach(({ tab }) => {
      const key = SHEET_TABS[tab];
      const data = dataMap.get(tab) || [];
      (result[key] as any) = data;

      // Create records from daily data
      if (key === SHEET_TABS.DAILY) {
        const records: Record<string, Campaign> = {};
        (data as DailyData[]).forEach((row: DailyData) => {
          if (row.campaign && !records[row.campaign]) {
            records[row.campaign] = {
              campaign: row.campaign,
              campaignId: row.campaignId,
              cost: row.cost,
              conv: row.conv,
              value: row.value,
              imprShare: row.imprShare,
            };
          }
        });
        result.records = records;
      }
    });

    return result;
  } catch (err) {
    console.error("Failed to load sample data:", err);
    return {
      ...emptyData,
      timestamp,
      dexieId: SAMPLE_ACCOUNT_ID,
    };
  }
}

async function loadCsvFile(
  tab: keyof typeof SHEET_TABS,
): Promise<Record<string, any>[]> {
  try {
    const filename = `${SHEET_TABS[tab].toLowerCase()}.csv`;
    // Use script version as cache buster to force refresh when data changes
    const cacheBuster = `?v=${SAMPLE_ACCOUNT_SCRIPT_VERSION}&t=${Date.now()}`;
    const fetchUrl = `/sample-data/${filename}${cacheBuster}`;

    const response = await fetch(fetchUrl, {
      cache: "no-cache",
      headers: {
        "Cache-Control": "no-cache, no-store, must-revalidate",
        Pragma: "no-cache",
        Expires: "0",
      },
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status} for ${filename}`);
    }

    const text = await response.text();

    return new Promise((resolve, reject) => {
      Papa.parse(text, {
        header: true,
        dynamicTyping: true,
        skipEmptyLines: true,
        complete: (results) => {
          if (results.errors.length > 0) {
            results.errors.forEach((error) => {
              console.warn(
                `PapaParse Error (${tab}, Row: ${error.row}): ${error.message} (${error.code})`,
              );
            });
          }
          resolve(results.data);
        },
        error: (error) => {
          console.error(`PapaParse critical error for tab ${tab}:`, error);
          reject(error);
        },
      });
    });
  } catch (err) {
    console.error(`Error loading/parsing CSV for tab ${tab}:`, err);
    return [];
  }
}

export async function loadSampleDataInBackground(): Promise<void> {
  try {
    // await new Promise(resolve => setTimeout(resolve, 1500)) // Comment out delay for faster testing

    // Load sample data
    const sampleData = await loadSampleData();

    // Store in DB
    await db.saveAccountData(SAMPLE_ACCOUNT_ID, sampleData);
  } catch (err) {
    console.error("Background sample data load failed:", err);
  }
}

const DAYS = [
  "SUNDAY",
  "MONDAY",
  "TUESDAY",
  "WEDNESDAY",
  "THURSDAY",
  "FRIDAY",
  "SATURDAY",
] as const;
type DayName = (typeof DAYS)[number]; // This creates a union type of the day names

export const getDayNumber = (dayName: string): number => {
  const upperDay = dayName.toUpperCase() as DayName;
  return DAYS.indexOf(upperDay);
};
